import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import weImage from '../assets/brand/we.jpg';
import cardImage from '../assets/brand/card.png';
import p1 from '../assets/brand/p1.svg';
import p2 from '../assets/brand/p2.svg';
import p3 from '../assets/brand/p3.svg';

const Services: React.FC = () => {
  const location = useLocation();

  React.useEffect(() => {
    if (!location.hash) {
      window.scrollTo(0, 0);
      return;
    }
    const id = location.hash.replace('#', '');
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [location.hash]);

  return (
    <div className="w-full bg-white">
      <section className="bg-white pb-10 pt-16">
        <div className="mx-auto max-w-[1360px] px-5">
          <div className="text-center">
            <Badge className="inline-flex rounded-full border border-[#B8B8B8] bg-white px-3 py-1 font-description text-[10px] font-normal text-[#9C9C9C]">
              Our Services
            </Badge>
            <h1 className="mt-3 font-title text-[42px] font-semibold leading-none text-[#212121] max-[700px]:text-[32px]">
              Explore Our services
            </h1>
            <p className="mx-auto mt-2 max-w-[560px] font-description text-[12px] font-light leading-[1.4] text-[#919191]">
              From product engineering to market expansion, we build and scale digital solutions that help
              organizations serve users better and grow with confidence.
            </p>
          </div>

          <div className="mx-auto mt-8 grid max-w-[1280px] grid-cols-3 gap-2 max-[760px]:grid-cols-1">
            <Card className="flex min-h-[250px] flex-col rounded-[20px] bg-gradient-to-b from-[#2776EA] to-[#164384] px-3.5 pb-4 pt-5 max-[980px]:min-h-[210px] max-[980px]:px-3 max-[980px]:pt-4">
              <div className="flex items-center">
                <img src={p1} alt="" aria-hidden="true" className="h-9 w-9 rounded-full border-2 border-[#2776EA] bg-white" />
                <img src={p2} alt="" aria-hidden="true" className="-ml-2 h-9 w-9 rounded-full border-2 border-[#2776EA] bg-white" />
                <img src={p3} alt="" aria-hidden="true" className="-ml-2 h-9 w-9 rounded-full border-2 border-[#2776EA] bg-white" />
              </div>

              <p className="mt-2.5 max-w-[210px] font-description text-[8px] font-light leading-[1.35] text-[#E4E4E4]">
                We design and ship reliable web and mobile platforms focused on performance, usability, and scale.
              </p>

              <div className="mt-10 h-px bg-[#E4E4E4]/35" />

              <div className="mt-auto pt-2">
                <h2 className="font-title text-[24px] font-medium leading-[1.08] text-white">
                  Web/Mobile
                  <br />
                  Development
                </h2>
                <p className="mt-2 max-w-[260px] font-description text-[11px] font-light leading-[1.45] text-[#E4E4E4]">
                  From customer portals to operational dashboards, we create mobile-ready software that fits your
                  workflows and delivers measurable impact.
                </p>
              </div>
            </Card>

            <Card
              className="relative overflow-hidden rounded-[20px] px-3.5 pb-4 pt-5"
              style={{
                backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.88) 100%), url(${cardImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            >
              <div className="min-h-[250px] flex items-end max-[980px]:min-h-[210px]">
                <div>
                  <h2 className="font-title text-[26px] font-medium leading-[1.08] text-white">
                    B2B Saas Solutions
                    <br />
                    &amp; API Development
                  </h2>
                  <p className="mt-2 max-w-[270px] font-description text-[11px] font-light leading-[1.45] text-white">
                    We create custom digital platforms for businesses that have outgrown basic tools. Each platform is
                    tailored to your operations, designed for your users and growth.
                  </p>
                </div>
              </div>
            </Card>

            <Card className="rounded-[20px] bg-[#212121] px-3.5 pb-4 pt-5 max-[980px]:px-3 max-[980px]:pt-4">
              <div className="flex min-h-[250px] items-center max-[980px]:min-h-[210px]">
                <div>
                  <h2 className="font-title text-[28px] font-medium leading-[1.08] text-white">
                    Digital
                    <br />
                    Marketing Services
                  </h2>
                  <p className="mt-2.5 max-w-[270px] font-description text-[11px] font-light leading-[1.45] text-white">
                    We run the digital marketing that brings your platform the attention it deserves and converts that
                    attention into real business results. From SEO and paid ads to content strategy and analytics, we
                    manage the full picture.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      <section id="web-mobile" className="bg-white pb-16 scroll-mt-24">
        <div className="layout-container max-w-[1360px] mx-auto px-5">
          <div className="rounded-[24px] bg-gradient-to-b from-[#2776EA] to-[#164384] p-8 md:p-12">
            <h2 className="font-title text-[28px] md:text-[36px] font-medium text-white leading-[1.1]">
              Web/Mobile Development
            </h2>
            <p className="mt-4 max-w-[720px] font-description text-[14px] font-light leading-[1.55] text-[#E4E4E4]">
              We design and ship reliable web and mobile platforms focused on performance, usability, and scale. From
              customer portals to operational dashboards, we create mobile-ready software that fits your workflows and
              delivers measurable impact.
            </p>
            <Link
              to="/contact?subject=Discuss%20a%20Project"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-white/20 px-5 py-2 font-title text-[12px] font-medium text-white hover:bg-white/30 transition-colors"
            >
              Start a Conversation
              <span aria-hidden="true">→</span>
            </Link>
          </div>
        </div>
      </section>

      <section id="saas-api" className="bg-white pb-16 scroll-mt-24">
        <div className="layout-container max-w-[1360px] mx-auto px-5">
          <div
            className="relative overflow-hidden rounded-[24px] p-8 md:p-12 min-h-[280px] flex items-end"
            style={{
              backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0) 30%, rgba(0,0,0,0.88) 100%), url(${cardImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          >
            <div>
              <h2 className="font-title text-[28px] md:text-[36px] font-medium text-white leading-[1.1]">
                B2B Saas Solutions &amp; API Development
              </h2>
              <p className="mt-4 max-w-[720px] font-description text-[14px] font-light leading-[1.55] text-white">
                We create custom digital platforms for businesses that have outgrown basic tools. Each platform is
                tailored to your operations, designed for your users and growth.
              </p>
              <Link
                to="/contact?subject=Discuss%20a%20Project"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#434343] px-5 py-2 font-title text-[12px] font-medium text-white hover:bg-[#555] transition-colors"
              >
                Start a Conversation
                <span aria-hidden="true">→</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section id="digital-marketing" className="bg-white pb-20 scroll-mt-24">
        <div className="layout-container max-w-[1360px] mx-auto px-5 grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
          <div className="rounded-[24px] bg-[#212121] p-8 md:p-12 flex flex-col justify-center">
            <h2 className="font-title text-[28px] md:text-[36px] font-medium text-white leading-[1.1]">
              Digital Marketing Services
            </h2>
            <p className="mt-4 font-description text-[14px] font-light leading-[1.55] text-white/90">
              We run the digital marketing that brings your platform the attention it deserves and converts that
              attention into real business results. From SEO and paid ads to content strategy and analytics, we manage
              the full picture.
            </p>
            <Link
              to="/contact?subject=Discuss%20a%20Project"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#434343] px-5 py-2 font-title text-[12px] font-medium text-white hover:bg-[#555] transition-colors w-fit"
            >
              Start a Conversation
              <span aria-hidden="true">→</span>
            </Link>
          </div>
          <div className="rounded-[24px] overflow-hidden min-h-[260px]">
            <img src={weImage} alt="Digital marketing collaboration" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      <section className="bg-[#F8FAFE] py-16">
        <div className="layout-container max-w-[800px] mx-auto px-5 text-center">
          <h2 className="font-title text-[28px] md:text-[34px] font-medium text-[#212121]">
            Ready to discuss a project?
          </h2>
          <p className="mt-3 font-description text-[14px] font-light text-[#919191]">
            Tell us what you need and we will help you choose the right path forward.
          </p>
          <Link to="/contact?subject=Discuss%20a%20Project">
            <Button className="mt-6 rounded-[8px] bg-[#2776EA] px-6 py-2.5 font-description text-[13px] font-medium text-white">
              Start a Conversation
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
